// src/components/metaAdmin/MetaAdminCard.js — cartão "Administração Meta".
//
// Out/26. Substitui no dashboard o antigo "Status do sistema" (roadmap
// estático, que saiu de vez — o cartão Meta tomou o lugar). Fiel ao design
// MetaAdminCard.dc.html: cabeçalho fixo (título, pílula de status, Ver
// tudo), abas minimalistas com sublinhado verde, corpo que troca com fade
// curto, rodapé fixo.
//
// Fase 1 do módulo: a aba Conta, o estado crítico (pagamento recusado ou
// número restrito) e os estados vazio / primeira leitura / defasado /
// falha. Fase 2 (out/26): abas Custo (a padrão) e Franquia. Fase 3 (out/26):
// aba Teto e o painel "Configurar teto" (ConfigurarTeto.js). Rastreamento de
// leads PR 3 (out/26): aba Anúncios, o interruptor do envio de "lead
// qualificado" e "venda" pra Meta. A lista de abas vem no payload, nunca aba
// vazia.
//
// Uma chamada só traz todas as abas: trocar de aba não pede nada ao
// servidor. O cartão relê a cada 60 s (aba visível) — é assim que um
// envio recusado por pagamento aparece aqui em menos de um minuto.
//
// Nenhum estado de erro esconde o que já foi carregado: dado de ontem é
// melhor que cartão em branco.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import ConfigurarTeto from './ConfigurarTeto';
import { C, FONT_NUM, FONT_TXT } from './tema';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowUpRight, FiBriefcase, FiCalendar, FiCheckCircle, FiExternalLink, FiPlus, FiRefreshCw, FiSmartphone,
} from 'react-icons/fi';
import api from '../../services/Api';


// Cor tem significado, nunca decoração (documento, Diretrizes visuais).
const TOM = {
  ok: { cor: C.green, glow: C.greenGlow, valor: C.text },
  atencao: { cor: C.amber, glow: 'rgba(255,170,0,.35)', valor: C.amber },
  critico: { cor: C.red, glow: 'rgba(255,69,96,.35)', valor: C.red },
  neutro: { cor: C.text3, glow: 'transparent', valor: C.text2 },
  info: { cor: C.blue, glow: 'transparent', valor: C.text },
};
const PILULA = {
  ok: [C.green, C.greenDim],
  atencao: [C.amber, C.amberDim],
  critico: [C.red, C.redDim],
  neutro: [C.text2, 'rgba(255,255,255,0.06)'],
  info: [C.blue, C.blueDim],
};
const ROTULO_ABA = { custo: 'Custo', teto: 'Teto', franquia: 'Franquia', conta: 'Conta', anuncios: 'Anúncios' };
const RELER_MS = 60000;
// Celular estreito: o cabeçalho e as abas se ajustam abaixo destas larguras.
const ATE_419 = '@media (max-width: 419.95px)';
const ATE_359 = '@media (max-width: 359.95px)';
const LINK_VER_TUDO = '/admin/meta';
// Sempre "custo estimado", nunca "fatura" — a Cetto não cobra essas
// mensagens (documento, Diretrizes de texto).
const AVISO_ESTIMATIVA = 'Custo estimado pela Cetto · a cobrança oficial é feita pela Meta';

// Fatias da rosca: escala do verde da marca, com UMA exceção — Serviço em
// âmbar, a única categoria com franquia e a que o gestor precisa vigiar.
const COR_CATEGORIA = {
  marketing: C.green,
  utilidade: 'rgba(0,214,124,0.55)',
  servico: C.amber,
  autenticacao: 'rgba(0,214,124,0.28)',
  outros: C.text3,
};

function haQuanto(iso, agora) {
  if (!iso) return '';
  const min = Math.max(0, Math.floor((agora - new Date(iso).getTime()) / 60000));
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `há ${h} h`;
  return `há ${Math.floor(h / 24)} dias`;
}

// Horas no fuso da OPERAÇÃO (vem no payload), não no relógio da máquina: o
// texto que chega pronto do servidor ("Desde hoje, 10:42") e o que montamos
// aqui (rodapé, faixa de falha) precisam dizer a mesma hora. Fuso inválido
// cai no do navegador em vez de quebrar o cartão.
function opcoesFuso(fuso, extra) {
  try {
    if (fuso) {
      new Intl.DateTimeFormat('pt-BR', { timeZone: fuso });
      return { ...extra, timeZone: fuso };
    }
  } catch (_) { /* fuso desconhecido pelo navegador */ }
  return extra;
}

function hora(iso, fuso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('pt-BR', opcoesFuso(fuso, { hour: '2-digit', minute: '2-digit' }));
}

function quandoCurto(iso, fuso) {
  if (!iso) return '';
  const dia = (d) => d.toLocaleDateString('pt-BR', opcoesFuso(fuso, { day: '2-digit', month: '2-digit', year: 'numeric' }));
  const d = new Date(iso);
  const agora = Date.now();
  if (dia(d) === dia(new Date(agora))) return `hoje, ${hora(iso, fuso)}`;
  if (dia(d) === dia(new Date(agora - 86400000))) return `ontem, ${hora(iso, fuso)}`;
  return `${d.toLocaleDateString('pt-BR', opcoesFuso(fuso, { day: '2-digit', month: '2-digit' }))}, ${hora(iso, fuso)}`;
}

const botaoTexto = {
  background: 'none', border: 'none', p: 0, cursor: 'pointer', fontFamily: FONT_TXT,
  '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2, borderRadius: '3px' },
};

export default function MetaAdminCard({ onConectar }) {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(false);
  const [aba, setAba] = useState(null);
  const [op, setOp] = useState(1);
  const [tentando, setTentando] = useState(false);
  const [fechando, setFechando] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());
  const [configurando, setConfigurando] = useState(false);
  const [mexendoAnuncios, setMexendoAnuncios] = useState(false);
  const [erroAnuncios, setErroAnuncios] = useState('');
  const fade = useRef(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get('/meta-admin/card');
      setDados(data);
      setErro(false);
    } catch {
      setErro(true); // mantém o que já estava na tela
    }
  }, []);

  useEffect(() => {
    carregar();
    const id = setInterval(() => {
      setAgora(Date.now());
      if (document.visibilityState === 'visible') carregar();
    }, RELER_MS);
    return () => { clearInterval(id); clearTimeout(fade.current); };
  }, [carregar]);

  const tentarDeNovo = async () => {
    if (tentando) return;
    setTentando(true);
    try {
      const { data } = await api.post('/meta-admin/sync');
      setDados(data);
      setErro(false);
    } catch {
      setErro(true);
    } finally {
      setTentando(false);
      setAgora(Date.now());
    }
  };

  const jaRegularizei = async (ids) => {
    if (fechando || !ids?.length) return;
    setFechando(true);
    try {
      let ultimo = null;
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post(`/meta-admin/events/${id}/ack`);
        ultimo = data;
      }
      if (ultimo) setDados(ultimo);
    } catch {
      setErro(true);
    } finally {
      setFechando(false);
    }
  };

  // Aba Anúncios: ligar/desligar o envio e "Tentar agora". A resposta é o
  // cartão inteiro, como a do teto.
  const mexerAnuncios = async (pedido) => {
    if (mexendoAnuncios) return;
    setMexendoAnuncios(true);
    setErroAnuncios('');
    try {
      const { data } = pedido === 'tentar'
        ? await api.post('/meta-admin/eventos-lead/tentar')
        : await api.put('/meta-admin/eventos-lead', { ligado: pedido === 'ligar' });
      setDados(data);
    } catch (e) {
      setErroAnuncios(e?.response?.data?.detail || 'Não foi possível salvar. Tente de novo.');
    } finally {
      setMexendoAnuncios(false);
    }
  };

  const abas = dados?.abas || [];
  const abaAtual = abas.includes(aba) ? aba : abas[0];
  const trocar = (id) => {
    if (id === abaAtual) return;
    clearTimeout(fade.current);
    setOp(0);
    fade.current = setTimeout(() => { setAba(id); setOp(1); }, 110);
  };

  const estado = dados?.estado;
  const critico = estado === 'critico' ? dados.critico : null;
  const vazio = estado === 'vazio';
  const comAbas = !!dados && !critico && !vazio && abas.length > 0;
  const [pCor, pBg] = PILULA[dados?.pilula?.tom] || PILULA.neutro;

  // ── Rodapé ────────────────────────────────────────────────────────────
  const sync = dados?.sync || {};
  const fuso = dados?.fuso;
  let rodapeEsq = AVISO_ESTIMATIVA;
  let rodapeCor = C.text2;
  let rodapeDir = sync.ok_em ? `Sincronizado ${haQuanto(sync.ok_em, agora)}` : '';
  // Custo, Teto e Franquia vêm da leitura de consumo (1 h), não da de
  // status (15 min): o "sincronizado há" dessas abas é o dela.
  if (comAbas && ['custo', 'teto', 'franquia'].includes(abaAtual)) {
    const quando = sync.consumo_em ? `sincronizado ${haQuanto(sync.consumo_em, agora)}` : '';
    const cambio = abaAtual === 'custo' ? dados.custo?.cambio : null;
    rodapeDir = [cambio, quando].filter(Boolean).join(' · ');
    if (!cambio && quando) rodapeDir = quando.charAt(0).toUpperCase() + quando.slice(1);
  }
  if (comAbas && abaAtual === 'anuncios') {
    // O que vai e o que não vai, numa linha (sem nome nem telefone: só o
    // clique do anúncio e o valor da venda).
    rodapeEsq = 'Vai só o clique do anúncio e o valor da venda · nada de nome nem telefone';
    rodapeDir = '';
  }
  if (estado === 'primeiro') {
    rodapeEsq = 'Primeira leitura da Meta em andamento · os dados chegam em até 15 minutos';
    rodapeCor = C.blue;
    rodapeDir = sync.conectado_em ? `Número conectado ${haQuanto(sync.conectado_em, agora)}` : '';
  }
  if (vazio) rodapeDir = '';
  if (critico) rodapeDir = critico.desde ? `Desde ${quandoCurto(critico.desde, fuso)}` : rodapeDir;
  if (dados?.dados === 'defasado' || dados?.dados === 'falha') {
    rodapeDir = sync.mais_antiga_em ? `Última sincronização ${haQuanto(sync.mais_antiga_em, agora)}` : rodapeDir;
  }
  const falhaSync = dados?.dados === 'falha' || (erro && !!dados);

  return (
    <Box
      data-screen-label="Administração Meta"
      sx={{
        // `flex` + `height: 100%`: no desktop a célula tem altura definida e o
        // cartão a ocupa; no celular a altura é só um mínimo e o cartão cresce
        // com a aba (ver AgentDash) — nada corta, em nenhuma largura.
        flex: '1 1 auto', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
        bgcolor: C.bg2, border: `1px solid ${critico ? C.redBdr : C.border}`, borderRadius: '10px',
        overflow: 'hidden', fontFamily: FONT_TXT, color: C.text, transition: 'border-color .2s',
        '@keyframes mcPulso': {
          '0%,100%': { opacity: 1, boxShadow: '0 0 5px rgba(255,69,96,.45)' },
          '50%': { opacity: 0.35, boxShadow: '0 0 2px rgba(255,69,96,.45)' },
        },
      }}
    >
      {/* Cabeçalho fixo. Celular (varredura de out/26): o título encolhia
          com reticências ("Admi…" em 320px) pra caber a pílula e o "Ver
          tudo". Agora o título nunca encolhe: abaixo de 420px o "Ver tudo"
          fica só com a seta (o nome segue no aria-label) e, se ainda não
          couber (320px, ou pílula longa como "Dados de 3 h atrás"), pílula
          e botão descem juntos pra uma segunda linha, à direita. */}
      <Box sx={{ p: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.2, rowGap: '6px', flexShrink: 0 }}>
        <Box sx={{ color: C.green, display: 'flex' }}><FiBriefcase size={14} /></Box>
        <Box sx={{ flex: '1 0 auto', fontSize: '0.82rem', fontWeight: 700, lineHeight: 1.5, whiteSpace: 'nowrap' }}>
          Administração Meta
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, ml: 'auto', flexShrink: 0 }}>
        {dados && (
          <Box component="span" sx={{ fontFamily: FONT_NUM, fontSize: '0.62rem', fontWeight: 600, p: '2px 6.4px', borderRadius: '4px', color: pCor, bgcolor: pBg, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {dados.pilula?.texto}
          </Box>
        )}
        <Box
          component="button" type="button" onClick={() => navigate(LINK_VER_TUDO)} aria-label="Ver tudo"
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: FONT_TXT, fontSize: '0.68rem', fontWeight: 700,
            lineHeight: 1.2, color: C.green, bgcolor: C.greenDim, border: '1px solid transparent', borderRadius: '6px',
            p: '4px 9.6px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
            [ATE_419]: { p: '4px', minWidth: 30, minHeight: 30 },
            '&:hover': { bgcolor: 'rgba(0,214,124,0.2)', borderColor: 'rgba(0,214,124,0.35)' },
            '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
          }}
        >
          <Box component="span" sx={{ [ATE_419]: { display: 'none' } }}>Ver tudo</Box> <FiArrowUpRight size={12} />
        </Box>
        </Box>
      </Box>

      {/* Abas */}
      {comAbas && (
        <Box role="tablist" aria-label="Administração Meta" sx={{
          // Abaixo de 360px (iPhone SE 1ª geração, 320) as 4 abas com 22px
          // de espaço passavam 23px da largura e "Conta" ficava escondida.
          display: 'flex', gap: '22px', [ATE_359]: { gap: '14px' }, px: 2, borderBottom: `1px solid ${C.border}`,
          overflowX: 'auto', scrollbarWidth: 'none', flexShrink: 0, '&::-webkit-scrollbar': { display: 'none' },
        }}>
          {abas.map((id) => {
            const ativa = id === abaAtual;
            return (
              <Box
                key={id} component="button" type="button" role="tab" aria-selected={ativa}
                onClick={() => trocar(id)}
                sx={{
                  background: 'none', border: 'none', borderBottom: `2px solid ${ativa ? C.green : 'transparent'}`,
                  p: '11px 0 9px', fontFamily: FONT_TXT, fontSize: '0.64rem', fontWeight: 700, letterSpacing: '.14em',
                  textTransform: 'uppercase', color: ativa ? C.text : C.text2, cursor: 'pointer', whiteSpace: 'nowrap',
                  flexShrink: 0, transition: 'color .15s', '&:hover': { color: C.text },
                  '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: -2 },
                }}
              >
                {ROTULO_ABA[id] || id}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Corpo */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column', opacity: op, transition: 'opacity .14s ease' }}>
        {!dados && !erro && <Carregando />}
        {!dados && erro && (
          <Box sx={{ flex: '1 1 auto', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1, p: '24px 22px' }}>
            <Box sx={{ fontSize: '0.82rem', fontWeight: 700 }}>Não foi possível carregar a Administração Meta.</Box>
            <Box component="button" type="button" onClick={carregar} sx={{ ...botaoTexto, alignSelf: 'flex-start', fontSize: '0.7rem', fontWeight: 700, color: C.green, '&:hover': { color: C.greenHover } }}>
              Tentar de novo
            </Box>
          </Box>
        )}
        {vazio && <Vazio onConectar={onConectar} />}
        {critico && <Critico c={critico} onJaRegularizei={jaRegularizei} fechando={fechando} />}
        {comAbas && abaAtual === 'custo' && <Custo c={dados.custo} />}
        {comAbas && abaAtual === 'teto' && <Teto t={dados.teto} onConfigurar={() => setConfigurando(true)} />}
        {comAbas && abaAtual === 'franquia' && <Franquia f={dados.franquia} navigate={navigate} />}
        {comAbas && abaAtual === 'conta' && <Conta linhas={dados.conta} navigate={navigate} />}
        {comAbas && abaAtual === 'anuncios' && (
          <Anuncios a={dados.anuncios} fuso={fuso} mexendo={mexendoAnuncios} erro={erroAnuncios} onMexer={mexerAnuncios} />
        )}
      </Box>

      {/* Faixa de falha de sincronização */}
      {falhaSync && (
        <Box sx={{ borderTop: `1px solid ${C.amberBdr}`, bgcolor: 'rgba(255,170,0,0.06)', p: '8px 16px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 14px', fontSize: '0.66rem', lineHeight: 1.5, flexShrink: 0 }}>
          <Box sx={{ flex: '1 1 260px', color: C.amber }}>
            {erro
              ? 'Não foi possível atualizar agora. Os dados abaixo são da última leitura.'
              : `Não foi possível ler a Meta ${sync.falhou_em ? `às ${hora(sync.falhou_em, fuso)}` : 'agora'}.${sync.ok_em ? ` Os dados abaixo são de ${quandoCurto(sync.mais_antiga_em || sync.ok_em, fuso)}.` : ''}`}
          </Box>
          <Box component="button" type="button" onClick={tentarDeNovo} disabled={tentando}
            sx={{ ...botaoTexto, display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.66rem', fontWeight: 700, color: C.green, '&:hover': { color: C.greenHover }, '&:disabled': { color: C.text2, cursor: 'default' } }}>
            <FiRefreshCw size={11} /> {tentando ? 'Tentando…' : 'Tentar de novo'}
          </Box>
        </Box>
      )}

      {/* Rodapé fixo */}
      <Box sx={{ borderTop: `1px solid ${C.border}`, p: '9px 16px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 16px', fontSize: '0.66rem', lineHeight: 1.5, flexShrink: 0 }}>
        <Box sx={{ flex: '1 1 auto', color: rodapeCor }}>{rodapeEsq}</Box>
        {rodapeDir && <Box sx={{ fontFamily: FONT_NUM, color: C.text3, whiteSpace: 'nowrap' }}>{rodapeDir}</Box>}
      </Box>

      {dados?.teto && (
        <ConfigurarTeto
          aberto={configurando}
          t={dados.teto}
          onFechar={() => setConfigurando(false)}
          onSalvo={(novo) => { setDados(novo); setErro(false); }}
        />
      )}
    </Box>
  );
}

function Carregando() {
  return (
    <Box aria-busy="true" sx={{ p: '20px 16px', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {[0, 1, 2, 3].map((i) => (
        <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ width: 150, height: 10, borderRadius: '4px', bgcolor: C.bg4 }} />
          <Box sx={{ flex: 1, height: 10, borderRadius: '4px', bgcolor: C.bg3 }} />
        </Box>
      ))}
    </Box>
  );
}

// Centraliza quando sobra espaço e rola quando falta. O design usava
// `justify-content: center`, que com conteúdo maior que a caixa transborda
// pros DOIS lados — no e2e de out/26, com a faixa âmbar de falha ocupando o
// rodapé, "Aprovada" sumia em cima e os templates embaixo, sem rolagem que
// alcançasse. `my: auto` no filho centraliza igual e vira 0 quando não cabe.
// `flex: 1 1 auto` junto do `height: 100%`: no celular o corpo do cartão
// não tem altura definida (o cartão cresce com o conteúdo) e o 100% vira
// auto — sem o flex o painel ficaria colado no topo em vez de centralizado.
const painel = {
  flex: '1 1 auto', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
  overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: `${C.bg4} transparent`,
};

function Rotulo({ cor, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5.6px', mb: '8px' }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: cor, flexShrink: 0 }} />
      <Box sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.16em', color: C.text3, textTransform: 'uppercase', lineHeight: 1.5 }}>
        {children}
      </Box>
    </Box>
  );
}

// Rosca de 4 fatias + "Outros" (<3%). O centro é a contagem de mensagens,
// não o valor: o valor já está à esquerda e repetir número cansa a leitura.
const ROSCA = 124;
const RAIO = 50;
const CIRC = 2 * Math.PI * RAIO;

function Rosca({ categorias, mensagens }) {
  let acumulado = 0;
  const fatias = (categorias || []).filter((c) => c.p > 0).map((c) => {
    const tam = c.p * CIRC;
    const f = { chave: c.chave, cor: COR_CATEGORIA[c.chave] || C.text3, dash: `${tam.toFixed(2)} ${(CIRC - tam).toFixed(2)}`, off: (-acumulado * CIRC).toFixed(2) };
    acumulado += c.p;
    return f;
  });
  const rotulo = (categorias || []).filter((c) => c.p > 0).map((c) => `${c.nome} ${c.pct}`).join(', ');
  return (
    <Box sx={{ flex: `0 0 ${ROSCA}px`, width: ROSCA, height: ROSCA, position: 'relative' }}>
      <svg width={ROSCA} height={ROSCA} viewBox={`0 0 ${ROSCA} ${ROSCA}`} role="img"
        aria-label={rotulo ? `Custo por categoria: ${rotulo}` : 'Sem custo no mês'}>
        <circle cx={ROSCA / 2} cy={ROSCA / 2} r={RAIO} fill="none" stroke={C.bg4} strokeWidth={12} />
        <g transform={`rotate(-90 ${ROSCA / 2} ${ROSCA / 2})`}>
          {fatias.map((f) => (
            <circle key={f.chave} cx={ROSCA / 2} cy={ROSCA / 2} r={RAIO} fill="none"
              stroke={f.cor} strokeWidth={12} strokeDasharray={f.dash} strokeDashoffset={f.off} />
          ))}
        </g>
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ fontFamily: FONT_NUM, fontSize: '1.2rem', fontWeight: 700, lineHeight: 1, color: C.text }}>{mensagens}</Box>
        <Box sx={{ fontSize: '0.52rem', fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: C.text2, mt: '5px' }}>mensagens</Box>
      </Box>
    </Box>
  );
}

function Custo({ c }) {
  if (!c) return null;
  return (
    <Box sx={{ ...painel, p: '12px 16px' }}>
      <Box sx={{ my: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px 26px' }}>
        <Box sx={{ flex: '1 1 170px', minWidth: 0 }}>
          <Rotulo cor={C.green}>Custo estimado · {c.mes}</Rotulo>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '5px', fontFamily: FONT_NUM, fontSize: '2.2rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-1px', color: C.text }}>
            {c.valor !== '—' && <Box component="span" sx={{ fontSize: '0.95rem', fontWeight: 400, letterSpacing: 0, color: C.text3 }}>R$</Box>}
            {c.valor}
          </Box>
          {c.usd && <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.7rem', color: C.text2, mt: '7px' }}>{c.usd}</Box>}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6.4px', mt: '9px', flexWrap: 'wrap' }}>
            {c.delta && (
              <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.62rem', fontWeight: 600, p: '2px 6.4px', borderRadius: '4px', color: C.text, bgcolor: 'rgba(255,255,255,0.07)' }}>
                {c.delta}
              </Box>
            )}
            <Box sx={{ fontSize: '0.7rem', color: C.text3 }}>{c.delta_txt}</Box>
          </Box>
          {c.aviso && <Box sx={{ fontSize: '0.64rem', color: C.amber, lineHeight: 1.45, mt: '8px', maxWidth: 260 }}>{c.aviso}</Box>}
          {/* Conta de cliente: valor pela tabela de preços (a Meta não informa
              o custo à Cetto). Cinza, não âmbar — é o normal, não um alerta. */}
          {c.nota && <Box sx={{ fontSize: '0.62rem', color: C.text2, lineHeight: 1.45, mt: '8px', maxWidth: 260 }}>{c.nota}</Box>}
          {/* Coexistência: o que saiu do app do celular não é cobrado e não
              entra no relatório da Meta. Contado ao vivo pela Cetto — sobe a
              cada releitura do cartão e some quando não houver. */}
          {c.app_celular && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '0.62rem', color: C.text2, lineHeight: 1.45, mt: '6px', maxWidth: 280 }}>
              <Box sx={{ display: 'flex', mt: '1px', flexShrink: 0 }}><FiSmartphone size={11} /></Box>
              <Box>{c.app_celular}</Box>
            </Box>
          )}
        </Box>

        <Rosca categorias={c.categorias} mensagens={c.mensagens} />

        <Box sx={{ flex: '1 1 230px', minWidth: 0 }}>
          {(c.categorias || []).map((cat) => (
            <Box key={cat.chave} sx={{ display: 'flex', alignItems: 'center', gap: '9.6px', py: '6px', borderBottom: `1px solid ${C.border}` }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '2px', flexShrink: 0, bgcolor: COR_CATEGORIA[cat.chave] || C.text3 }} />
              <Box sx={{ flex: 1, minWidth: 0, fontSize: '0.75rem', color: C.text }}>{cat.nome}</Box>
              <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.75rem', fontWeight: 600, color: C.text }}>{cat.valor}</Box>
              <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.69rem', color: C.text2, width: 36, textAlign: 'right' }}>{cat.pct}</Box>
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9.6px', pt: '8px' }}>
            <Box sx={{ width: 8, flexShrink: 0 }} />
            <Box sx={{ flex: 1, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: C.text2 }}>Total estimado</Box>
            <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.75rem', fontWeight: 700, color: C.text }}>{c.total}</Box>
            <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.69rem', color: C.text2, width: 36, textAlign: 'right' }}>{c.total_pct}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ── Teto ───────────────────────────────────────────────────────────────
// Barra de consumo do mês contra o limite, com os marcadores 50/80/100. A
// cor segue o tom que vem do servidor (verde < 80%, âmbar ≥ 80%, vermelho
// ≥ 100%). Qualquer supervisor configura (decisão de out/26) — o design
// previa "só o administrador", e a frase saiu junto.
const MARCAS = [50, 80, 100];
const posMarca = (v) => (v === 100 ? 'calc(100% - 2px)' : `${v}%`);
const txMarca = (v) => (v === 100 ? 'translateX(-100%)' : 'translateX(-50%)');

const botaoConfigurar = {
  display: 'flex', alignItems: 'center', gap: '6px', fontFamily: FONT_TXT, fontSize: '0.7rem', fontWeight: 700,
  color: C.green, bgcolor: C.greenDim, border: `1px solid ${C.greenBdr}`, borderRadius: '6px', p: '6.4px 12.8px',
  '@media (pointer: coarse)': { minHeight: 38 }, // alvo de toque (celular e tablet)
  cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(0,214,124,0.2)' },
  '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
};

function Caixa({ rotulo, children, flex = '1 1 120px' }) {
  return (
    <Box sx={{ flex, minWidth: 0, bgcolor: C.bg3, borderRadius: '6px', p: '7px 12px' }}>
      <Box sx={{ fontSize: '0.55rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: C.text3 }}>{rotulo}</Box>
      {children}
    </Box>
  );
}

function Teto({ t, onConfigurar }) {
  if (!t) return null;
  if (!t.configurado) {
    return (
      <Box sx={{ ...painel, p: '16px' }}>
        <Box sx={{ my: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Rotulo cor={C.text3}>Teto mensal</Rotulo>
          <Box>
            <Box sx={{ height: 14, borderRadius: '7px', border: '1px dashed rgba(255,255,255,0.12)', boxSizing: 'border-box' }} />
            <Box sx={{ position: 'relative', height: 16, mt: '8px' }}>
              {MARCAS.map((v) => (
                <Box key={v} sx={{ position: 'absolute', top: 0, left: posMarca(v), transform: txMarca(v), fontFamily: FONT_NUM, fontSize: '0.64rem', fontWeight: 600, color: C.text3 }}>{v}%</Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px 20px' }}>
            <Box sx={{ flex: '1 1 240px', fontSize: '0.8rem', color: C.text, lineHeight: 1.5, textWrap: 'pretty' }}>
              Defina quanto a empresa pode gastar com a Meta por mês e receba aviso em 50%, 80% e 100%.
            </Box>
            <Box component="button" type="button" onClick={onConfigurar} sx={botaoConfigurar}>Configurar teto</Box>
          </Box>
        </Box>
      </Box>
    );
  }

  const tom = TOM[t.tom] || TOM.ok;
  const lido = t.lido !== false;
  const largura = lido ? Math.max(0, Math.min(100, t.largura || 0)) : 0;
  const ligados = new Set((t.marcadores || []).filter((m) => m.on).map((m) => m.v));
  // Sem leitura da Meta a barra fica vazia: marcador "passou" e projeção em
  // âmbar contradiriam a barra.
  const passou = new Set(lido ? (t.marcadores || []).filter((m) => m.passou).map((m) => m.v) : []);
  const projecaoAcima = lido && !!t.projecao && !!t.projecao_txt && !t.projecao_txt.startsWith('fecha');
  // gap 10 (o design pede 18): o cartão real tem ~345px, não os 505 do
  // design — com 12 a linha do bloqueio ficava 8px abaixo da borda (e2e de
  // out/26), igual ao que já tinha acontecido com Conta e Franquia.
  return (
    <Box sx={{ ...painel, p: '12px 16px' }}>
      <Box sx={{ my: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '6px 20px' }}>
          <Box>
            <Rotulo cor={lido ? tom.cor : C.text3}>Teto mensal · {t.escopo_txt}</Rotulo>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
              <Box sx={{ fontFamily: FONT_NUM, fontSize: '2.2rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-1px', color: C.text }}>
                {lido ? t.pct : '—'}
                {lido && <Box component="span" sx={{ fontSize: '1rem', fontWeight: 400, letterSpacing: 0, color: C.text3, ml: '1px' }}>%</Box>}
              </Box>
              <Box sx={{ fontSize: '0.7rem', color: C.text2 }}>
                {lido ? `do teto consumido em ${t.mes}` : 'Aguardando a primeira leitura da Meta'}
              </Box>
            </Box>
          </Box>
          {t.ultimo_aviso && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.68rem', color: C.text2 }}>
              <Box sx={{ display: 'flex', color: C.green }}><FiCheckCircle size={13} /></Box>{t.ultimo_aviso}
            </Box>
          )}
        </Box>

        <Box>
          <Box role="progressbar" aria-label={`Teto: ${lido ? `${t.pct}% consumido` : 'sem leitura'}`}
            aria-valuemin={0} aria-valuemax={100} aria-valuenow={lido ? Math.min(100, t.pct) : 0}
            sx={{ position: 'relative', height: 14, borderRadius: '7px', bgcolor: C.bg4 }}>
            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${largura}%`, borderRadius: '7px', bgcolor: tom.cor, boxShadow: `0 0 10px ${tom.glow}`, transition: 'width .4s ease' }} />
            {MARCAS.map((v) => (
              <Box key={v} sx={{ position: 'absolute', top: -4, bottom: -4, width: 2, borderRadius: '1px', left: posMarca(v),
                bgcolor: passou.has(v) ? C.bg2 : 'rgba(255,255,255,0.28)', opacity: ligados.has(v) ? 1 : 0.35 }} />
            ))}
          </Box>
          <Box sx={{ position: 'relative', height: 16, mt: '8px' }}>
            {MARCAS.map((v) => (
              <Box key={v} sx={{ position: 'absolute', top: 0, left: posMarca(v), transform: txMarca(v), whiteSpace: 'nowrap',
                fontFamily: FONT_NUM, fontSize: '0.64rem', fontWeight: 600,
                color: !ligados.has(v) ? C.text3 : passou.has(v) ? tom.cor : C.text2 }}>
                {v}%
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <Caixa rotulo="Consumido">
            <Box sx={{ fontFamily: FONT_NUM, fontSize: '1rem', fontWeight: 700, color: C.text, mt: '2px' }}>{lido ? t.consumido : '—'}</Box>
          </Caixa>
          <Caixa rotulo="Teto">
            <Box sx={{ fontFamily: FONT_NUM, fontSize: '1rem', fontWeight: 700, color: C.text, mt: '2px' }}>{t.limite}</Box>
          </Caixa>
          <Caixa rotulo="Projeção · fim do mês" flex="1.4 1 180px">
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', mt: '2px' }}>
              <Box sx={{ fontFamily: FONT_NUM, fontSize: '1rem', fontWeight: 700, color: projecaoAcima ? C.amber : C.text }}>
                {lido && t.projecao ? t.projecao : '—'}
              </Box>
              <Box sx={{ fontSize: '0.64rem', color: C.text2 }}>
                {lido && t.projecao ? t.projecao_txt : 'a partir do 7º dia do mês'}
              </Box>
            </Box>
          </Caixa>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '0.7rem', color: t.bloqueado ? C.red : C.text2 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, bgcolor: t.bloqueado ? C.red : t.bloqueio ? C.green : C.text3 }} />
            {t.bloqueado ? `${t.bloqueado_txt} · resposta a cliente segue liberada` : t.bloqueio_txt}
          </Box>
          <Box component="button" type="button" onClick={onConfigurar} sx={botaoConfigurar}>Configurar teto</Box>
        </Box>
      </Box>
    </Box>
  );
}

// Barra por número: verde até 800, âmbar até 999, vermelha ao zerar a
// franquia (design). Com um número só (Starter), a barra fica única e
// grossa — a aba não pode parecer vazia (documento, Aba 3).
function corDoUso(usado, limite) {
  if (usado >= limite) return C.red;
  if (usado >= limite * 0.8) return C.amber;
  return C.green;
}

function Franquia({ f, navigate }) {
  if (!f) return null;
  const um = f.numeros?.length === 1 && !f.mais;
  const limite = f.limite || 1000;
  // O cartão real tem ~345px (a altura da coluna TMA + Ranking), não os
  // 505 do design: aviso âmbar + 3 números passava 43px e a última linha
  // sumia (e2e de out/26). Com 3 números o aviso sai — o "+N cobradas ·
  // R$ X" já está na linha do número estourado, em âmbar.
  const comAviso = !!f.excedente && (f.numeros?.length || 0) <= 2;
  return (
    <Box sx={{ ...painel, p: '10px 16px' }}>
      <Box sx={{ my: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '6px 20px' }}>
          <Box>
            <Rotulo cor={C.amber}>Franquia de serviço · {f.mes}</Rotulo>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
              <Box sx={{ fontFamily: FONT_NUM, fontSize: '1.85rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-1px', color: C.text }}>{f.restante}</Box>
              <Box sx={{ fontSize: '0.7rem', color: C.text2 }}>{f.restante_txt}</Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.68rem', color: C.text2 }}>
            <FiCalendar size={13} /> {f.renova}
          </Box>
        </Box>

        {comAviso && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', p: '6px 12px', borderRadius: '6px', bgcolor: C.amberDim, border: `1px solid ${C.amberBdr}`, fontSize: '0.7rem', color: C.text, lineHeight: 1.45 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.amber, flexShrink: 0 }} />
            <Box>
              <Box component="b" sx={{ color: C.amber }}>{f.excedente.quem}</Box>
              {f.excedente.varios ? ' passaram' : ' passou'} das {limite.toLocaleString('pt-BR')} gratuitas:{' '}
              <Box component="b" sx={{ color: C.amber, fontFamily: FONT_NUM }}>{f.excedente.cobradas}</Box> mensagens cobradas
              {/* Sem valor quando a Meta não informou o custo — nunca "R$ 0,00". */}
              {f.excedente.valor ? (
                <>, <Box component="b" sx={{ color: C.amber, fontFamily: FONT_NUM }}>{f.excedente.valor}</Box> estimados.</>
              ) : '.'}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {(f.numeros || []).map((n, i, a) => {
            const ultima = i === a.length - 1 && !f.mais;
            return (
              <Box key={`${n.nome}-${n.numero}`} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 16px', py: '6px', borderBottom: `1px solid ${ultima ? 'transparent' : C.border}` }}>
                <Box sx={{ flex: '0 0 150px', minWidth: 0 }}>
                  <Box sx={{ fontSize: '0.75rem', fontWeight: 700, color: C.text, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.nome}</Box>
                  <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.65rem', color: C.text3 }}>{n.numero}</Box>
                </Box>
                <Box role="progressbar" aria-label={`${n.nome}: ${n.usado_txt} de ${limite.toLocaleString('pt-BR')} gratuitas`}
                  aria-valuemin={0} aria-valuemax={limite} aria-valuenow={n.usado}
                  sx={{ flex: '1 1 160px', minWidth: 120, bgcolor: C.bg4, overflow: 'hidden', height: um ? 14 : 6, borderRadius: um ? '7px' : '3px' }}>
                  <Box sx={{ height: '100%', width: `${Math.min(100, (n.usado / limite) * 100)}%`, bgcolor: corDoUso(n.usado, limite), borderRadius: 'inherit' }} />
                </Box>
                <Box sx={{ flex: '0 0 auto', minWidth: 92, textAlign: 'right' }}>
                  <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.75rem', fontWeight: 600, color: C.text }}>
                    {n.usado_txt}<Box component="span" sx={{ color: C.text3, fontWeight: 400 }}> / {limite.toLocaleString('pt-BR')}</Box>
                  </Box>
                  <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.62rem', color: n.cobradas ? C.amber : C.text3 }}>{n.sub}</Box>
                </Box>
              </Box>
            );
          })}
          {f.mais > 0 && (
            <Box component="button" type="button" onClick={() => navigate(LINK_VER_TUDO)}
              sx={{ ...botaoTexto, alignSelf: 'flex-start', mt: '6px', fontSize: '0.68rem', fontWeight: 700, color: C.green, '&:hover': { color: C.greenHover } }}>
              e mais {f.mais} {f.mais === 1 ? 'número' : 'números'} · Ver tudo →
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Anúncios (rastreamento de leads PR 3, out/26) ─────────────────────
// Quando o atendente marca um lead de anúncio "Clique para o WhatsApp" como
// qualificado ou venda fechada, a Cetto avisa a Meta (QualifiedLead /
// Purchase). Desligado até um supervisor ligar; ligar inclui os últimos 7
// dias. Sem a permissão da Meta no número, os eventos esperam na fila.
const SITUACAO = {
  ativo: { tom: 'ok', rotulo: 'Envio ligado' },
  aguardando_permissao: { tom: 'atencao', rotulo: 'Aguardando a permissão da Meta' },
  erro: { tom: 'atencao', rotulo: 'A Meta recusou o último envio' },
  desligado: { tom: 'neutro', rotulo: 'Envio desligado' },
};
const plural = (n, um, varios) => `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`;

function Anuncios({ a, fuso, mexendo, erro, onMexer }) {
  if (!a) return null;
  const sit = SITUACAO[a.situacao] || SITUACAO.desligado;
  const tom = TOM[sit.tom] || TOM.neutro;
  const m = a.marcados || {};
  const comClique = m.com_clique || 0;
  const semClique = m.sem_clique || 0;
  const paradas = (a.contas || []).filter((c) => c.estado === 'sem_permissao' || c.estado === 'erro');
  const linkTexto = {
    ...botaoTexto, fontSize: '0.7rem', fontWeight: 700, color: C.text2,
    '&:hover': { color: C.text }, '&:disabled': { opacity: 0.5, cursor: 'default' },
  };

  if (!a.ligado) {
    return (
      <Box sx={{ ...painel, p: '14px 16px' }}>
        <Box sx={{ my: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Rotulo cor={C.text3}>Lead qualificado e venda para a Meta</Rotulo>
          <Box sx={{ fontSize: '0.8rem', color: C.text, lineHeight: 1.5, textWrap: 'pretty' }}>
            Quando um lead de anúncio é marcado como qualificado ou venda fechada, a Cetto avisa a Meta — e ela
            passa a mandar mais gente parecida.
          </Box>
          <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.7rem', color: C.text2, lineHeight: 1.5 }}>
            Nos últimos 7 dias: {plural(comClique, 'marcação de anúncio pode ir', 'marcações de anúncio podem ir')}
            {semClique > 0 && ` · ${plural(semClique, 'sem clique de anúncio não vai', 'sem clique de anúncio não vão')}`}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 16px' }}>
            <Box component="button" type="button" onClick={() => onMexer('ligar')} disabled={mexendo} sx={botaoConfigurar}>
              {mexendo ? 'Ligando…' : 'Ligar o envio'}
            </Box>
            <Box sx={{ fontSize: '0.66rem', color: C.text2 }}>Inclui as marcações dos últimos 7 dias.</Box>
          </Box>
          {erro && <Box role="alert" sx={{ fontSize: '0.7rem', color: C.red }}>{erro}</Box>}
        </Box>
      </Box>
    );
  }

  const s7 = a.semana || {};
  return (
    <Box sx={{ ...painel, p: '12px 16px' }}>
      <Box sx={{ my: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Rotulo cor={tom.cor}>{sit.rotulo}</Rotulo>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
          <Box sx={{ fontFamily: FONT_NUM, fontSize: '2.2rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-1px', color: C.text }}>
            {(s7.enviados || 0).toLocaleString('pt-BR')}
          </Box>
          <Box sx={{ fontSize: '0.7rem', color: C.text2 }}>
            {s7.enviados === 1 ? 'evento enviado' : 'eventos enviados'} nos últimos 7 dias
            {s7.na_fila > 0 && ` · ${plural(s7.na_fila, 'na fila', 'na fila')}`}
          </Box>
        </Box>

        {a.situacao === 'aguardando_permissao' && (
          <Box sx={{ fontSize: '0.72rem', color: C.amber, lineHeight: 1.5, textWrap: 'pretty' }}>
            A Meta ainda não liberou o envio para {paradas.length > 1 ? 'estes números' : 'este número'}
            {paradas.length ? ` (${paradas.flatMap((c) => c.numeros || []).map((n) => `+${n}`).join(', ')})` : ''}.
            Os eventos esperam na fila por até 7 dias. Depois de reconectar o número, toque em Tentar agora.
          </Box>
        )}
        {a.situacao === 'erro' && (
          <Box sx={{ fontSize: '0.72rem', color: C.amber, lineHeight: 1.5 }}>
            {paradas[0]?.erro || 'A Meta recusou o último envio.'} A Cetto tenta de novo sozinha.
          </Box>
        )}
        {semClique > 0 && (
          <Box sx={{ fontSize: '0.66rem', color: C.text2, lineHeight: 1.5 }}>
            {plural(semClique, 'marcação', 'marcações')} dos últimos 7 dias sem clique de anúncio (lead orgânico ou
            anúncio no Status) não {semClique === 1 ? 'vai' : 'vão'}.
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 18px' }}>
          {(a.situacao === 'aguardando_permissao' || a.situacao === 'erro') && (
            <Box component="button" type="button" onClick={() => onMexer('tentar')} disabled={mexendo} sx={botaoConfigurar}>
              <FiRefreshCw size={12} /> {mexendo ? 'Tentando…' : 'Tentar agora'}
            </Box>
          )}
          <Box component="button" type="button" onClick={() => onMexer('desligar')} disabled={mexendo} sx={linkTexto}>
            Desligar o envio
          </Box>
          {a.alterado_em && (
            <Box sx={{ fontSize: '0.64rem', color: C.text3 }}>
              Ligado{a.alterado_por ? ` por ${a.alterado_por}` : ''} {quandoCurto(a.alterado_em, fuso)}
            </Box>
          )}
        </Box>
        {erro && <Box role="alert" sx={{ fontSize: '0.7rem', color: C.red }}>{erro}</Box>}
      </Box>
    </Box>
  );
}

function Conta({ linhas, navigate }) {
  return (
    <Box sx={{ ...painel, px: '16px' }}>
      <Box sx={{ my: 'auto' }}>
      {(linhas || []).map((r, i, a) => {
        const t = TOM[r.tom] || TOM.neutro;
        return (
          // py 9px (o design pede 12): o cartão acompanha a altura da coluna
          // TMA + Ranking (~345px em 1440) e com 12px as quatro linhas não
          // cabiam por ~18px — a última ficava cortada no estado normal.
          // Com 9px sobram ~10px de folga pra fonte/navegador diferente.
          <Box key={r.chave} sx={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 14px', py: '9px',
            borderBottom: `1px solid ${i < a.length - 1 ? C.border : 'transparent'}`,
          }}>
            <Box sx={{ flex: '0 0 178px', display: 'flex', alignItems: 'center', gap: '9px' }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, bgcolor: t.cor, boxShadow: `0 0 5px ${t.glow}` }} />
              <Box sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{r.rotulo}</Box>
            </Box>
            <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
              <Box sx={{ fontSize: '0.78rem', fontWeight: 600, lineHeight: 1.4, color: t.valor }}>{r.valor}</Box>
              {r.detalhe && <Box sx={{ fontSize: '0.68rem', color: C.text2, lineHeight: 1.5, mt: '1px' }}>{r.detalhe}</Box>}
            </Box>
            {r.link && (
              <Box component="button" type="button" onClick={() => navigate(r.link)}
                sx={{ ...botaoTexto, fontSize: '0.68rem', fontWeight: 700, color: C.green, whiteSpace: 'nowrap', '&:hover': { color: C.greenHover } }}>
                {r.link_rotulo || 'Ver'} →
              </Box>
            )}
          </Box>
        );
      })}
      </Box>
    </Box>
  );
}

function Critico({ c, onJaRegularizei, fechando }) {
  return (
    <Box role="alert" sx={{ flex: '1 1 auto', height: '100%', display: 'flex', alignItems: 'stretch', background: 'linear-gradient(90deg, rgba(255,69,96,0.13) 0%, transparent 58%)' }}>
      <Box sx={{ width: 2, bgcolor: C.red, opacity: 0.9, flexShrink: 0 }} />
      <Box sx={{ ...painel, flex: 1, p: '24px 22px', minWidth: 0 }}>
       <Box sx={{ my: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: 580 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5.6px', flexWrap: 'wrap' }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: C.red, flexShrink: 0, animation: 'mcPulso 2.4s ease-in-out infinite', '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }} />
          <Box sx={{ fontSize: '0.64rem', fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: C.red, lineHeight: 1.2 }}>{c.rotulo}</Box>
          {c.chip && <Box sx={{ fontSize: '0.58rem', fontWeight: 600, color: C.text3, letterSpacing: '.04em', whiteSpace: 'nowrap' }}>· {c.chip}</Box>}
        </Box>
        <Box sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, fontWeight: 700, lineHeight: 1.3, textWrap: 'pretty' }}>{c.titulo}</Box>
        <Box sx={{ fontSize: '0.78rem', color: C.text2, lineHeight: 1.6, textWrap: 'pretty' }}>{c.texto}</Box>
        <Box sx={{ fontSize: '0.72rem', fontWeight: 600, color: C.red, opacity: 0.88, lineHeight: 1.4 }}>
          <Box component="span" sx={{ opacity: 0.6, mr: '5px' }}>→</Box>{c.acao}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px', mt: 0.75 }}>
          <Box component="a" href={c.link} target="_blank" rel="noopener noreferrer"
            sx={{
              display: 'flex', alignItems: 'center', gap: '7px', fontFamily: FONT_TXT, fontSize: '0.7rem', fontWeight: 700,
              color: C.red, bgcolor: C.redDim, border: `1px solid ${C.redBdr}`, borderRadius: '6px', p: '7px 13px',
              textDecoration: 'none', '&:hover': { bgcolor: 'rgba(255,69,96,0.2)' },
              '&:focus-visible': { outline: `2px solid ${C.red}`, outlineOffset: 2 },
            }}>
            {c.botao} <FiExternalLink size={12} />
          </Box>
          {c.eventos?.length > 0 && (
            <Box component="button" type="button" onClick={() => onJaRegularizei(c.eventos)} disabled={fechando}
              sx={{ ...botaoTexto, fontSize: '0.7rem', fontWeight: 600, color: C.text2, '&:hover': { color: C.text }, '&:disabled': { cursor: 'default' } }}>
              {fechando ? 'Fechando…' : 'Já regularizei'}
            </Box>
          )}
        </Box>
       </Box>
      </Box>
    </Box>
  );
}

function Vazio({ onConectar }) {
  return (
    <Box sx={{ ...painel, p: '24px 22px' }}>
     <Box sx={{ my: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: 560 }}>
      <Box sx={{ fontSize: '1.05rem', fontWeight: 800, letterSpacing: '-.2px', lineHeight: 1.35, textWrap: 'pretty' }}>
        Conecte o primeiro número para acompanhar a conta na Meta
      </Box>
      <Box sx={{ fontSize: '0.82rem', color: C.text2, lineHeight: 1.6, textWrap: 'pretty' }}>
        Status da conta, faixa de envio, pagamento e templates aparecem aqui assim que o número estiver ativo. Fluxo oficial Meta, leva 2 minutos.
      </Box>
      {onConectar && (
        <Box sx={{ display: 'flex', mt: 1 }}>
          <Box component="button" type="button" onClick={onConectar}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, fontFamily: FONT_TXT, fontSize: '0.7rem', fontWeight: 700,
              letterSpacing: '.02em', color: '#060d14', bgcolor: C.green, border: 'none', borderRadius: '6px', p: '8px 14px',
              cursor: 'pointer', '&:hover': { bgcolor: C.greenHover },
              '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
            }}>
            <FiPlus size={13} /> Conectar número
          </Box>
        </Box>
      )}
     </Box>
    </Box>
  );
}
